import { useMutation } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { startRegistration } from "@simplewebauthn/browser";
import { webauthnApi } from "../api/webauthnApi";
import { finalizeWebauthnLogin } from "../../../redux/actions/auth";
import { WEBAUTHN_MOCK_CEREMONY } from "../config";
import { usePreauthExpiryGuard } from "../usePreauthExpiryGuard";
import { getDeviceFingerprint } from "../deviceFingerprint";

/**
 * Attestation ceremony (Phase 3 of enrollment): options -> credentials.create
 * -> verify -> persist credential. If the backend grants a session at
 * completion (spec §20.7), the response carries a token and we finalize;
 * otherwise the caller routes the user to a normal assertion.
 */
export function useRegisterCeremony() {
  const dispatch = useDispatch();
  const guardPreauth = usePreauthExpiryGuard();
  const ticket = useSelector((s) => s.auth.webauthn?.preauth_ticket);

  return useMutation(
    async ({ requestId, label }) => {
      const optionsJSON = await webauthnApi.registerOptions(requestId, ticket);
      const attestation = WEBAUTHN_MOCK_CEREMONY
        ? { mock: true }
        : await startRegistration({ optionsJSON });
      // Fingerprint after the ceremony (gesture-safe); memoized → instant.
      const fingerprint = await getDeviceFingerprint();
      return webauthnApi.registerVerify(requestId, attestation, label, fingerprint, ticket);
    },
    {
      onSuccess: (tokenPayload) => {
        if (tokenPayload && tokenPayload.token) {
          dispatch(finalizeWebauthnLogin(tokenPayload));
        }
      },
      onError: (error) => guardPreauth(error),
    }
  );
}
